import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";
import { format } from "date-fns";

interface PrenatalIntake {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  due_date: string | null;
  medicaid_id: string | null;
}

interface PNCCCarePlanSheetProps {
  open: boolean;
  onClose: () => void;
  intake: PrenatalIntake;
}

const STRENGTH_OPTIONS = [
  "Ability to Meet Personal Goals",
  "Knowledge or Education",
  "Life Experience",
  "Love for Child or Children",
  "Love for Self",
  "Motivation/Determination/Follow-Through",
  "Personal Relationships",
  "Positive Attitude",
  "Religious Beliefs or Spiritual Practice",
  "Resourcefulness",
  "Sense of Humor",
];

const MENTAL_HEALTH_CONCERNS = [
  "Concerns About Pregnancy",
  "Drugs, Alcohol, or Tobacco Use",
  "Mental Health Concerns",
  "Pregnancy History",
  "Support System",
  "Religious, Ethnic, or Cultural Factor Affecting Pregnancy",
];

const SUPPORT_PEOPLE = [
  "Partner or Spouse",
  "Parents",
  "Siblings",
  "Extended Family",
  "Friends",
  "Home Visitors",
];

interface CarePlanItem {
  need: string;
  clientDesire: boolean;
  actionSteps: string;
  frequency: string;
  goals: string;
}

export function PNCCCarePlanSheet({ open, onClose, intake }: PNCCCarePlanSheetProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [carePlanType, setCarePlanType] = useState<"initial" | "updated">("initial");
  const [updateReason, setUpdateReason] = useState("");
  const [selectedStrengths, setSelectedStrengths] = useState<string[]>([]);
  const [otherStrengths, setOtherStrengths] = useState("");
  const [riskFactorsList, setRiskFactorsList] = useState("");
  const [selectedMentalHealth, setSelectedMentalHealth] = useState<string[]>([]);
  const [selectedSupportPeople, setSelectedSupportPeople] = useState<string[]>([]);
  const [relationshipPlan, setRelationshipPlan] = useState("");
  const [carePlanItems, setCarePlanItems] = useState<CarePlanItem[]>([
    { need: "", clientDesire: true, actionSteps: "", frequency: "", goals: "" }
  ]);
  const [providerSignature, setProviderSignature] = useState("");

  const { data: existingCarePlan, isLoading } = useQuery({
    queryKey: ["pncc-care-plan", intake.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pncc_care_plans")
        .select("*")
        .eq("prenatal_intake_id", intake.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");

      const carePlanData = {
        prenatal_intake_id: intake.id,
        member_id: intake.id, // This should be the user_id from the intake form
        provider_id: user.id,
        care_plan_type: carePlanType,
        update_reason: carePlanType === "updated" ? updateReason : null,
        member_strengths: selectedStrengths,
        other_strengths: otherStrengths || null,
        risk_factors_list: riskFactorsList || null,
        mental_health_concerns: selectedMentalHealth,
        support_people: selectedSupportPeople,
        relationship_plan: relationshipPlan || null,
        care_plan_items: JSON.parse(JSON.stringify(carePlanItems)),
        provider_signature: providerSignature || null,
        provider_signature_date: providerSignature ? new Date().toISOString().split("T")[0] : null,
        status: "active",
      };

      if (existingCarePlan) {
        const { error } = await supabase
          .from("pncc_care_plans")
          .update(carePlanData)
          .eq("id", existingCarePlan.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("pncc_care_plans")
          .insert(carePlanData);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success("Care plan saved successfully");
      queryClient.invalidateQueries({ queryKey: ["pncc-care-plan", intake.id] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to save care plan");
    },
  });

  const addCarePlanItem = () => {
    setCarePlanItems([
      ...carePlanItems,
      { need: "", clientDesire: true, actionSteps: "", frequency: "", goals: "" }
    ]);
  };

  const removeCarePlanItem = (index: number) => {
    if (carePlanItems.length > 1) {
      setCarePlanItems(carePlanItems.filter((_, i) => i !== index));
    }
  };

  const updateCarePlanItem = (index: number, field: keyof CarePlanItem, value: any) => {
    const updated = [...carePlanItems];
    updated[index] = { ...updated[index], [field]: value };
    setCarePlanItems(updated);
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>PNCC Care Plan</SheetTitle>
          <SheetDescription>
            {intake.first_name} {intake.last_name} • Medicaid ID: {intake.medicaid_id || "N/A"}
            {intake.due_date && ` • Due: ${format(new Date(intake.due_date), "MMM d, yyyy")}`}
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Tabs defaultValue="strengths" className="mt-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="strengths">Strengths</TabsTrigger>
              <TabsTrigger value="health">Health Info</TabsTrigger>
              <TabsTrigger value="support">Support</TabsTrigger>
              <TabsTrigger value="plan">Care Plan</TabsTrigger>
            </TabsList>

            <TabsContent value="strengths" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Strength-Based Assessment</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="mb-2 block">Member's Strengths and Abilities</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {STRENGTH_OPTIONS.map((strength) => (
                        <div key={strength} className="flex items-center space-x-2">
                          <Checkbox
                            id={strength}
                            checked={selectedStrengths.includes(strength)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedStrengths([...selectedStrengths, strength]);
                              } else {
                                setSelectedStrengths(selectedStrengths.filter((s) => s !== strength));
                              }
                            }}
                          />
                          <label htmlFor={strength} className="text-sm">{strength}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="other-strengths">Other Strengths</Label>
                    <Textarea
                      id="other-strengths"
                      value={otherStrengths}
                      onChange={(e) => setOtherStrengths(e.target.value)}
                      placeholder="Describe other strengths..."
                      className="mt-1"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="health" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Health Information from Assessment</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="risk-factors">Risk Factors and Needs</Label>
                    <Textarea
                      id="risk-factors"
                      value={riskFactorsList}
                      onChange={(e) => setRiskFactorsList(e.target.value)}
                      placeholder="List risk factors identified in the assessment..."
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block">Mental Health Concerns</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {MENTAL_HEALTH_CONCERNS.map((concern) => (
                        <div key={concern} className="flex items-center space-x-2">
                          <Checkbox
                            id={concern}
                            checked={selectedMentalHealth.includes(concern)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedMentalHealth([...selectedMentalHealth, concern]);
                              } else {
                                setSelectedMentalHealth(selectedMentalHealth.filter((c) => c !== concern));
                              }
                            }}
                          />
                          <label htmlFor={concern} className="text-sm">{concern}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="support" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Relationships and Social Support</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="mb-2 block">People who can help meet care plan goals</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {SUPPORT_PEOPLE.map((person) => (
                        <div key={person} className="flex items-center space-x-2">
                          <Checkbox
                            id={person}
                            checked={selectedSupportPeople.includes(person)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedSupportPeople([...selectedSupportPeople, person]);
                              } else {
                                setSelectedSupportPeople(selectedSupportPeople.filter((p) => p !== person));
                              }
                            }}
                          />
                          <label htmlFor={person} className="text-sm">{person}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="relationship-plan">Plan to Strengthen Relationships</Label>
                    <Textarea
                      id="relationship-plan"
                      value={relationshipPlan}
                      onChange={(e) => setRelationshipPlan(e.target.value)}
                      placeholder="Describe the plan to strengthen relationships and social supports..."
                      className="mt-1"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="plan" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Care Plan Items</CardTitle>
                    <Button variant="outline" size="sm" onClick={addCarePlanItem}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Item
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {carePlanItems.map((item, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">Item {index + 1}</Badge>
                        {carePlanItems.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCarePlanItem(index)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Need Identified</Label>
                          <Input
                            value={item.need}
                            onChange={(e) => updateCarePlanItem(index, "need", e.target.value)}
                            placeholder="Describe the need..."
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            checked={item.clientDesire}
                            onCheckedChange={(checked) => updateCarePlanItem(index, "clientDesire", checked)}
                          />
                          <Label>Client desires to address</Label>
                        </div>
                      </div>
                      <div>
                        <Label>Action Steps</Label>
                        <Textarea
                          value={item.actionSteps}
                          onChange={(e) => updateCarePlanItem(index, "actionSteps", e.target.value)}
                          placeholder="Describe action steps..."
                          className="min-h-[60px]"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Frequency of Service</Label>
                          <Input
                            value={item.frequency}
                            onChange={(e) => updateCarePlanItem(index, "frequency", e.target.value)}
                            placeholder="e.g., Weekly, Monthly"
                          />
                        </div>
                        <div>
                          <Label>Goals and Outcomes</Label>
                          <Input
                            value={item.goals}
                            onChange={(e) => updateCarePlanItem(index, "goals", e.target.value)}
                            placeholder="Expected outcomes..."
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="pt-4 border-t">
                    <Label htmlFor="provider-signature">Provider Signature</Label>
                    <Input
                      id="provider-signature"
                      value={providerSignature}
                      onChange={(e) => setProviderSignature(e.target.value)}
                      placeholder="Type your full name as signature"
                      className="mt-1 font-serif italic"
                    />
                  </div>

                  <Button
                    className="w-full"
                    onClick={() => saveMutation.mutate()}
                    disabled={saveMutation.isPending}
                  >
                    {saveMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save Care Plan
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
}
